<template>
  <div
    class="absolute bottom-2! right-1/2 z-20 flex w-full max-w-[980px] translate-x-1/2 justify-end"
  >
    <div class="mr-4 flex flex-col items-end gap-2">
      <div
        v-if="unreadMentionsCount > 0"
        class="relative"
      >
        <wui-btn
          class="chat-scroll-fab rounded-full! bg-comptheme-overlaybtns-bg! hover:bg-comptheme-overlaybtns-bg! text-comptheme-overlaybtns-icon!"
          data-test="chat-scroll-to-mention"
          variant="neut"
          state="alpha"
          icon
          prepend-icon="atM"
          @click="onMentionClick"
        >
          <span class="sr-only select-none">Scroll to mention</span>
        </wui-btn>
        <app-badge
          class="absolute -top-1 -right-1"
          :value="unreadMentionsCount"
        />
      </div>

      <div
        v-if="unreadReactionsCount > 0"
        class="relative"
      >
        <wui-btn
          class="chat-scroll-fab rounded-full! bg-comptheme-overlaybtns-bg! hover:bg-comptheme-overlaybtns-bg! text-comptheme-overlaybtns-icon!"
          data-test="chat-scroll-to-reaction"
          variant="neut"
          state="alpha"
          icon
          prepend-icon="heartM"
          @click="onReactionClick"
        >
          <span class="sr-only select-none">Scroll to reaction</span>
        </wui-btn>
        <app-badge
          class="absolute -top-1 -right-1"
          :value="unreadReactionsCount"
        />
      </div>

      <div class="relative">
        <wui-btn
          class="chat-scroll-fab rounded-full! bg-comptheme-overlaybtns-bg! hover:bg-comptheme-overlaybtns-bg! text-comptheme-overlaybtns-icon!"
          data-test="chat-scroll-down"
          variant="neut"
          state="alpha"
          icon
          prepend-icon="chevronDownLgM"
          @click="$emit('click', $event)"
        >
          <span class="sr-only select-none">Scroll down</span>
        </wui-btn>
        <app-badge
          v-if="unreadEventsCount > 0"
          class="absolute -top-1 -right-1"
          :value="unreadEventsCount"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { WuiBtn } from '@wui/common-library'
import { MessageIncomingEvent, PServed } from '@wui/im'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'


import { chatModel } from '@/entities/messages'
import { useNotificationItemStore } from '@/entities/notification'
import { useStoreRooms, useStoreLastReadEventIds } from '@/entities/room'

import { AppBadge } from '@/shared/components/app-badge'
import { useCurrentUser } from '@/shared/composables/current-user'

const props = defineProps<{
  pServed: PServed
}>()

const emit = defineEmits<{
  click: [event: MouseEvent]
  'scroll-to-mention': [eventId: number]
  'scroll-to-reaction': [eventId: number]
}>()

const storeRooms = useStoreRooms()
const { getUnreadCount, findRoomMessages, findById } = chatModel.useChatMessageStore()
const { getOwnMessageRankByRoomAndContactId } = useStoreLastReadEventIds()
const { currentUser } = useCurrentUser()
const { notifications } = storeToRefs(useNotificationItemStore())

const unreadEventsCount = computed(() => {
  const room = storeRooms.findByPServed(props.pServed)
  return room ? getUnreadCount(room.pServedUser) : 0
})

const unreadMentionEventIds = computed(() => {
  const currentImLogin = currentUser.value?.imLogin
  if (!currentImLogin) {
    return [] as number[]
  }

  const lastReadRank = getOwnMessageRankByRoomAndContactId(props.pServed)
  const ids: number[] = []

  findRoomMessages(props.pServed).forEach((message) => {
    if (!message.rank || message.rank <= lastReadRank) {
      return
    }
    if (message.item.type !== 'IncomingMessageEvent') {
      return
    }
    const incoming = message.item as MessageIncomingEvent
    const isMentioned = Boolean(incoming.mentionedUsers?.includes(currentImLogin))
    const isQuoteOfOwn = Boolean(
      incoming.quote?.quotedEventId
      && findById(incoming.quote.quotedEventId)?.item.type === 'OutgoingMessageEvent',
    )
    if (isMentioned || isQuoteOfOwn) {
      ids.push(message.eventId)
    }
  })

  return ids
})

const unreadMentionsCount = computed(() => unreadMentionEventIds.value.length)

const unreadReactionTargetEventIds = computed(() => {
  const ids = new Set<number>()
  notifications.value.forEach((notification) => {
    if (
      notification.unread
      && notification.type === 'AddReaction'
      && notification.pServedUser === props.pServed
      && notification.targetEventId
    ) {
      ids.add(notification.targetEventId)
    }
  })
  return [...ids].sort((a, b) => a - b)
})

const unreadReactionsCount = computed(() => unreadReactionTargetEventIds.value.length)

const onMentionClick = () => {
  const eventId = unreadMentionEventIds.value[0]
  if (eventId) {
    emit('scroll-to-mention', eventId)
  }
}

const onReactionClick = () => {
  const eventId = unreadReactionTargetEventIds.value[0]
  if (eventId) {
    emit('scroll-to-reaction', eventId)
  }
}
</script>

<style scoped>
/* shadow-overlay (e500) with blur radii reduced by ~52% (0.7 × 0.8 × 0.85) */
.chat-scroll-fab {
  box-shadow:
    0 0 0.24px rgba(9, 10, 11, 0.08),
    0 2px 2.38px rgba(9, 10, 11, 0.15),
    0 10px 11.42px rgba(9, 10, 11, 0.18) !important;
}
</style>
